import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { B2bVehicleSubstitution } from '../../../models/Contract';
import { ContractsService } from '../../../services/contracts.service';
import { ConfirmationService } from '../../../shared/services/confirmation.service';
import { ErrorService } from '../../../shared/services/error.service';
import { TableAction } from '../../../shared/components/table/table.component';
import { B2bContractPlateOption } from '../b2b-contract-plate-select/b2b-contract-plate-select.component';
import { exportB2bTableToExcel } from '../../../utils/b2b-table-export.util';

@Component({
  selector: 'app-b2b-substitutions',
  templateUrl: './b2b-substitutions.component.html',
  styleUrls: ['../shared/b2b-operational-shared.css', './b2b-substitutions.component.css']
})
export class B2bSubstitutionsComponent implements OnInit {
  contractId: number | null = null;
  contractNumber = '';
  contractName = '';
  items: B2bVehicleSubstitution[] = [];
  isLoading = false;
  submitting = false;
  showModal = false;
  viewMode = false;
  editingId: number | null = null;
  selectedOldVehicleId: number | null = null;
  selectedNewVehicleId: number | null = null;
  form!: FormGroup;

  columns = [
    { titulo: 'Placa antiga', label: 'oldPlate' },
    { titulo: 'Placa nova', label: 'newPlate' },
    { titulo: 'Data', label: 'substitutedAt', formatter: (v: any) => this.formatDate(v) },
    { titulo: 'Observações', label: 'notes' }
  ];

  actions: TableAction[] = [
    { label: 'Visualizar', icon: 'fas fa-eye', action: 'view', class: 'btn-view' },
    { label: 'Editar', icon: 'fas fa-edit', action: 'edit', class: 'btn-edit' },
    { label: 'Excluir', icon: 'fas fa-trash', action: 'delete', class: 'btn-delete' }
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private contractsService: ContractsService,
    private errorService: ErrorService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      oldPlate: [''],
      newPlate: [''],
      substitutedAt: [''],
      notes: ['']
    });

    this.route.params.subscribe(params => {
      this.contractId = +params['id'];
      if (this.contractId) {
        this.loadContractInfo();
        this.loadItems();
      }
    });
  }

  get modalTitle(): string {
    if (this.viewMode) return 'Visualizar substituição';
    return this.editingId ? 'Editar substituição' : 'Nova substituição';
  }

  loadContractInfo(): void {
    if (!this.contractId) return;
    this.contractsService.getB2bContractById(this.contractId).subscribe({
      next: (c) => {
        this.contractNumber = c.contractNumber || '';
        this.contractName = c.contractName || '';
      },
      error: (err) => this.errorService.handleHttpError(err)
    });
  }

  loadItems(): void {
    if (!this.contractId) return;
    this.isLoading = true;
    this.contractsService.getB2bSubstitutions(this.contractId).subscribe({
      next: (list) => {
        this.items = (list || []).map(x => this.normalize(x));
        this.isLoading = false;
      },
      error: () => { this.isLoading = false; }
    });
  }

  openCreate(): void {
    this.viewMode = false;
    this.editingId = null;
    this.selectedOldVehicleId = null;
    this.selectedNewVehicleId = null;
    this.form.enable();
    this.form.reset({ oldPlate: '', newPlate: '', substitutedAt: '', notes: '' });
    this.showModal = true;
  }

  openView(item: B2bVehicleSubstitution): void {
    this.patchForm(item);
    this.viewMode = true;
    this.form.disable();
    this.showModal = true;
  }

  openEdit(item: B2bVehicleSubstitution): void {
    this.patchForm(item);
    this.viewMode = false;
    this.form.enable();
    this.showModal = true;
  }

  private patchForm(item: B2bVehicleSubstitution): void {
    this.editingId = item.id ?? null;
    this.selectedOldVehicleId = item.oldVehicleId ?? null;
    this.selectedNewVehicleId = item.newVehicleId ?? null;
    this.form.patchValue({
      oldPlate: item.oldPlate || '',
      newPlate: item.newPlate || '',
      substitutedAt: this.toDateInput(item.substitutedAt),
      notes: item.notes || ''
    });
  }

  onOldPlateSelected(option: B2bContractPlateOption | null): void {
    this.selectedOldVehicleId = option?.vehicleId ?? null;
  }

  onNewPlateSelected(option: B2bContractPlateOption | null): void {
    this.selectedNewVehicleId = option?.vehicleId ?? null;
  }

  closeModal(): void {
    this.showModal = false;
    this.viewMode = false;
    this.editingId = null;
    this.selectedOldVehicleId = null;
    this.selectedNewVehicleId = null;
    this.form.enable();
  }

  submit(): void {
    if (this.viewMode || !this.contractId || this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const raw = this.form.getRawValue();
    const payload: B2bVehicleSubstitution = {
      b2bContractId: this.contractId,
      oldPlate: raw.oldPlate || null,
      newPlate: raw.newPlate || null,
      substitutedAt: raw.substitutedAt ? new Date(raw.substitutedAt).toISOString() : null,
      oldVehicleId: this.selectedOldVehicleId,
      newVehicleId: this.selectedNewVehicleId,
      notes: raw.notes || null
    };

    this.submitting = true;
    const req$ = this.editingId
      ? this.contractsService.updateB2bSubstitution(this.contractId, this.editingId, { ...payload, id: this.editingId })
      : this.contractsService.createB2bSubstitution(this.contractId, payload);

    req$.subscribe({
      next: () => {
        this.submitting = false;
        this.errorService.showSuccessMessage(this.editingId ? 'Substituição atualizada.' : 'Substituição criada.');
        this.closeModal();
        this.loadItems();
      },
      error: () => { this.submitting = false; }
    });
  }

  onActionClick(event: { action: string; item: B2bVehicleSubstitution }): void {
    if (event.action === 'view') this.openView(event.item);
    if (event.action === 'edit') this.openEdit(event.item);
    if (event.action === 'delete') this.deleteItem(event.item);
  }

  deleteItem(item: B2bVehicleSubstitution): void {
    if (!this.contractId || !item.id) return;
    const label = [item.oldPlate, item.newPlate].filter(Boolean).join(' → ') || 'esta substituição';
    this.confirmationService.confirmDelete(label, 'a substituição').subscribe({
      next: (ok) => {
        if (!ok) return;
        this.contractsService.deleteB2bSubstitution(this.contractId!, item.id!).subscribe({
          next: () => {
            this.errorService.showSuccessMessage('Substituição excluída.');
            this.loadItems();
          }
        });
      }
    });
  }


  exportExcel(): void {
    exportB2bTableToExcel(
      `b2b-substituicoes-${this.contractNumber || this.contractId}`,
      this.columns,
      this.items,
      'Substituicoes'
    );
  }

  navigateBack(): void {
    if (this.contractId) this.router.navigate(['/b2b/contracts', this.contractId, 'view']);
    else this.router.navigate(['/b2b/contracts']);
  }

  private normalize(dto: any): B2bVehicleSubstitution {
    return {
      id: dto.id ?? dto.Id,
      b2bContractId: dto.b2bContractId ?? dto.b2BContractId ?? dto.B2BContractId ?? this.contractId!,
      oldPlate: dto.oldPlate ?? dto.OldPlate ?? dto.oldVehiclePlate ?? dto.OldVehiclePlate ?? null,
      newPlate: dto.newPlate ?? dto.NewPlate ?? dto.newVehiclePlate ?? dto.NewVehiclePlate ?? null,
      substitutedAt: dto.substitutedAt ?? dto.SubstitutedAt ?? null,
      oldVehicleId: dto.oldVehicleId ?? dto.OldVehicleId ?? null,
      newVehicleId: dto.newVehicleId ?? dto.NewVehicleId ?? null,
      notes: dto.notes ?? dto.Notes ?? null
    };
  }

  private toDateInput(value: any): string {
    if (!value) return '';
    const d = new Date(value);
    return isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
  }

  formatDate(value: any): string {
    if (!value) return '—';
    try { return new Date(value).toLocaleDateString('pt-BR'); } catch { return String(value); }
  }
}
